import React, { useRef, useImperativeHandle, useEffect, useState } from "react";
import ShippingAddressSelect, {
    ShippingAddressSelectInstance,
} from "@/scenes/manualOrder/Add/components/ShippingAddressSelect";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import { ManualOrderItem } from "@/scenes/manualOrder/Add/components/OrderItemForm/type";
import { observer } from "mobx-react-lite";
import OrderItemFormStore from "@/scenes/manualOrder/Add/components/OrderItemForm/OrderItemFormStore";
import OrderItemForm from "@/scenes/manualOrder/Add/components/OrderItemForm";
import {
    EncryptedConsigneeInfoFormInstance,
    EncryptedConsigneeInfoFormValues,
} from "../EncryptedConsigneeInfoForm/type";
import SelectManualShopForm, { SelectManualShopFormInstance } from "../../SelectManualShopForm";
import ConsigneeInfoForm from "../EncryptedConsigneeInfoForm";

interface CreateManualOrderFormProps {
    initialValues?: {
        consignee: Partial<EncryptedConsigneeInfoFormValues>;
        orderItems: ManualOrderItem[];
        shipperAddressId?: number;
    };
}

export interface CreateEncryptedManualOrderFormInstance {
    getFormValues(): Promise<{
        shopId: number;
        consignee: EncryptedConsigneeInfoFormValues;
        orderItems: ManualOrderItem[];
        shippingAddress?: ShippingAddressDTO;
    }>;
    setConsigneeInfoFormValues(data: Partial<EncryptedConsigneeInfoFormValues>): void;
    setOrderItemsValues(orderItems: ManualOrderItem[]): void;
    setShipperAddressId(shippingAddressId: number): void;
}

const CreateEncryptedManualOrderForm: React.ForwardRefRenderFunction<
    CreateEncryptedManualOrderFormInstance,
    CreateManualOrderFormProps
> = function (props, ref) {
    const { initialValues } = props;
    const selectShopForm = useRef<SelectManualShopFormInstance>();
    const consigneeInfoForm = useRef<EncryptedConsigneeInfoFormInstance>();
    const shippingAddressRef = useRef<ShippingAddressSelectInstance>();

    const [orderItemFormStore] = useState(() => new OrderItemFormStore());

    async function getFormValues() {
        const { shopId } = await selectShopForm.current.validateFields();

        const consignee = await consigneeInfoForm.current.getFormValues();

        const orderItems = await orderItemFormStore.getSelectedOrderItems();

        const shippingAddressId = shippingAddressRef.current.shipperAddressId;
        const shippingAddress = shippingAddressRef.current.shippingAddresses.find(({ id }) => id === shippingAddressId);

        return {
            shopId,
            consignee,
            orderItems,
            shippingAddress,
        };
    }

    useImperativeHandle(ref, () => ({
        getFormValues,
        setConsigneeInfoFormValues(data) {
            consigneeInfoForm.current?.setFieldsValue(data);
        },
        setOrderItemsValues(orderItems) {
            orderItemFormStore.setOrderItems(orderItems);
        },
        setShipperAddressId(shippingAddressId) {
            shippingAddressRef.current?.setShipperAddressId(shippingAddressId);
        },
    }));

    useEffect(() => {
        if (!initialValues) return;

        consigneeInfoForm.current?.setFieldsValue(initialValues.consignee);
        orderItemFormStore.setOrderItems(initialValues.orderItems);

        if (initialValues.shipperAddressId !== undefined) {
            shippingAddressRef.current?.setShipperAddressId(initialValues.shipperAddressId);
        }
    }, [initialValues]);

    useEffect(() => {
        const selectedKeys = orderItemFormStore.orderItems.map(({ key }) => key);
        orderItemFormStore.setSelectedRowKeys(selectedKeys);
    }, [orderItemFormStore.orderItems]);

    return (
        <div>
            <SelectManualShopForm ref={selectShopForm} />

            <div className="flex mt30">
                <div style={{ flex: "0 0 auto" }}>
                    <ConsigneeInfoForm ref={consigneeInfoForm} />
                </div>

                <div className="f1 ml20">
                    <ShippingAddressSelect ref={shippingAddressRef} />

                    <OrderItemForm
                        store={orderItemFormStore}
                        hideDelete
                        hideHistoryAddedManualOrderItemList
                        hideCheckbox={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default observer(React.forwardRef(CreateEncryptedManualOrderForm));
